(function(global) {
  'use strict';
  const PROPERTY = 'Material';
  const sortedProperties = properties => Object.keys(properties || {}).filter(key => key !== PROPERTY)
    .sort().map(key => [key, properties[key]]);
  const identity = line => JSON.stringify([
    String(line.variant_id || line.id),
    String(line.selling_plan_id || (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan && line.selling_plan_allocation.selling_plan.id) || 0),
    sortedProperties(line.properties)
  ]);
  function inventory(lines) {
    const totals = new Map();
    for (const line of lines) {
      if (!Number.isInteger(line.quantity) || line.quantity < 1) throw new Error('Invalid line quantity');
      const id = identity(line);
      totals.set(id, (totals.get(id) || 0) + line.quantity);
    }
    return JSON.stringify([...totals.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }
  function linesMatch(snapshot, cart) {
    if (!cart || !Array.isArray(cart.items) || snapshot.lines.length !== cart.items.length) return false;
    return snapshot.lines.every(line => cart.items.some(item => item.key === line.key &&
      identity(item) === identity(line) && item.quantity === line.quantity &&
      JSON.stringify(Object.entries(item.properties || {}).sort()) === JSON.stringify(Object.entries(line.properties || {}).sort())));
  }
  async function synchronize(api) {
    let snapshot = await api.snapshot();
    if (!snapshot || !Array.isArray(snapshot.lines) || snapshot.lines.length === 0) throw new Error('Empty or invalid cart');
    const originalInventory = inventory(snapshot.lines);
    // One change per freshly read line. The bound also protects against an API
    // silently refusing/truncating properties or an endlessly changing cart.
    const limit = snapshot.lines.length + 2;
    for (let attempt = 0; attempt <= limit; attempt++) {
      if (inventory(snapshot.lines) !== originalInventory) throw new Error('Cart changed during checkout preparation');
      const cart = await api.cart();
      if (inventory(cart.items || []) !== originalInventory || !linesMatch(snapshot, cart)) {
        throw new Error('Cart changed during checkout preparation');
      }
      const target = snapshot.lines.find(line => {
        if (typeof line.material !== 'string') throw new Error('Unsupported material source');
        return line.material.length > 0 && (line.properties || {})[PROPERTY] !== line.material;
      });
      if (!target) return cart;
      const preparedQuantity = state => state.lines.filter(line => identity(line) === identity(target) &&
        (line.properties || {})[PROPERTY] === target.material).reduce((sum, line) => sum + line.quantity, 0);
      const preparedBefore = preparedQuantity(snapshot);
      const properties = { ...(target.properties || {}), [PROPERTY]: target.material };
      // Use the exact line key, never variant ID; retain personalization and files.
      // Shopify defaults an omitted quantity to 1. Preserve the freshly checked line quantity.
      // The before/after reads detect observable concurrent changes; this is not an atomic lock.
      await api.change({ id: target.key, quantity: target.quantity, properties });
      snapshot = await api.snapshot();
      if (preparedQuantity(snapshot) !== preparedBefore + target.quantity) {
        throw new Error('Material property was not retained exactly or cart changed');
      }
    }
    throw new Error('Cart did not stabilize');
  }
  function singleFlight(task) {
    let pending = null;
    return function() {
      if (!pending) pending = Promise.resolve().then(task).finally(() => { pending = null; });
      return pending;
    };
  }
  function checkoutFields(entries, editedNote) {
    const update = {}, attributes = {};
    let discount = null;
    for (const [name, value] of entries) {
      if (name === 'note') update.note = String(value);
      else if (name === 'discount') discount = String(value);
      else {
        const match = /^attributes\[(.+)\]$/.exec(name);
        if (match) Object.defineProperty(attributes, match[1], {value:String(value), enumerable:true});
      }
    }
    if (Object.keys(attributes).length) update.attributes = attributes;
    if (editedNote !== null) update.note = editedNote;
    return {update, discount};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { synchronize, inventory, linesMatch, singleFlight, checkoutFields };
  if (!global.document) return;
  const configElement = document.getElementById('nized-checkout-materials-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const root = ((global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || config.root).replace(/\/?$/, '/');
  let busy = false;
  let dialog;
  let editedNote = null;
  async function request(path, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(root + path, Object.assign({ cache: 'no-store', credentials: 'same-origin' }, options, { signal: controller.signal }));
      if (!response.ok) throw new Error('Cart request failed');
      return response;
    } finally { clearTimeout(timer); }
  }
  const api = {
    async snapshot() {
      const response = await request('cart?section_id=nized-checkout-materials');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = parsed.querySelector('[data-nized-checkout-materials]');
      if (!element) throw new Error('Material section unavailable');
      return JSON.parse(element.textContent);
    },
    async cart() { return (await request('cart.js')).json(); },
    async change(payload) {
      await request('cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
    },
    async update(payload) {
      await request('cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
    }
  };
  function showStatus(failed) {
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.style.cssText = 'max-width:32rem;padding:1.5rem;background:#111;color:#fff;border:1px solid #777;z-index:2147483647;';
      document.body.appendChild(dialog);
      dialog.addEventListener('cancel', event => { if (busy) event.preventDefault(); });
    }
    dialog.replaceChildren();
    const message = document.createElement('p');
    message.textContent = failed ? config.failed : config.pending;
    message.setAttribute('role', failed ? 'alert' : 'status');
    dialog.appendChild(message);
    dialog.setAttribute('aria-label', failed ? config.failed : config.pending);
    if (failed) {
      const button = document.createElement('button');
      button.type = 'button'; button.textContent = config.close;
      button.addEventListener('click', () => dialog.close());
      dialog.appendChild(button);
    }
    if (!dialog.open) dialog.showModal();
  }
  const prepare = singleFlight(() => synchronize(api));
  async function startCheckout(destination, form) {
    if (busy) return;
    busy = true;
    showStatus(false);
    try {
      const fields = checkoutFields(form ? new FormData(form).entries() : [], editedNote);
      if (fields.discount !== null) {
        const target = new URL(destination, global.location.href);
        target.searchParams.set('discount', fields.discount);
        destination = target.href;
      }
      if (Object.keys(fields.update).length) {
        if (fields.update.attributes) {
          const before = await api.cart();
          fields.update.attributes = { ...(before.attributes || {}), ...fields.update.attributes };
        }
        await api.update(fields.update);
      }
      const preparedCart = await prepare();
      if (Object.keys(fields.update).length) {
        const finalCart = await api.cart();
        if (!linesMatch({lines:preparedCart.items}, finalCart)) throw new Error('Cart changed before checkout');
        if (Object.hasOwn(fields.update, 'note') && finalCart.note !== fields.update.note) throw new Error('Cart note not retained');
        for (const [name, value] of Object.entries(fields.update.attributes || {})) {
          if ((finalCart.attributes || {})[name] !== value) throw new Error('Cart attribute not retained');
        }
      }
      // Keep the modal and busy state until navigation completes.
      global.location.assign(destination);
    } catch (error) {
      busy = false;
      showStatus(true);
    }
  }
  function checkoutUrl(value) {
    try {
      const url = new URL(value, global.location.href);
      return url.origin === global.location.origin && /\/checkout\/?$/.test(url.pathname) ? url.href : null;
    } catch (error) { return null; }
  }
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const link = target.closest('a[href]');
    const destination = link && checkoutUrl(link.href);
    if (destination) {
      event.preventDefault(); event.stopImmediatePropagation();
      startCheckout(destination);
      return;
    }
    if (busy && target.closest('button, input, select, [data-action], product-form-component')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('input', event => {
    const target = event.target;
    if (target && (target.name === 'note' || target.id === 'nz-cart-note' || target.id === 'nz-cart-note-summary')) {
      editedNote = target.value;
    }
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    const destination = checkoutUrl(form.action) || (submitter && submitter.name === 'checkout' ? root + 'checkout' : null);
    if (destination) {
      event.preventDefault(); event.stopImmediatePropagation();
      startCheckout(destination, form);
    } else if (busy) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
})(typeof window === 'undefined' ? globalThis : window);
